/**
 * Snapchat Ads Fixture Generator (Story 19.9)
 *
 * 1. WHAT: Generates AdapterMetrics matching Story 19.8's fetchMetrics() shape
 *    for 1 demo client (TrendWear Fashion DTC).
 * 2. CLIENT ARCHETYPE: DTC fashion / Gen-Z scale (~2.3% swipe-up rate, ~$0.25
 *    eCPSU, ~5.3× ROAS, female 18-34 dominant) — per April 2026 Snap fashion
 *    benchmarks (Snapchat for Business 2025 review, BeProfit, Megadigital).
 * 3. CROSS-RECORD-TYPE INVARIANTS:
 *    - swipeUps / impressions ≈ swipeUpRate ± 0.0001 per row
 *    - videoViews2s ≥ videoQuartile1 ≥ videoQuartile2 ≥ videoQuartile3 ≥ viewCompletion
 *    - conversionXApp + conversionXWeb === conversionX ± 0.5% (top 4 events)
 *    - Campaigns sum matches overview within ±0.5% (volume metrics only;
 *      derived metrics like swipeUpRate / conversionRate are computed
 *      independently per row)
 *    - AdSquads sum matches parent campaign within ±0.5%
 *    - Percentages in [0, 1]
 *    - No `_derived_*` apiField metrics (calculated client-side: ecpm, ecpsu,
 *      costPerInstall, roas)
 *    - No raw Snap snake_case field names (screen_time_millis, swipe_up_percent,
 *      conversion_purchases_app — all transformed to camelCase per
 *      Story 19.8 transformer FIELD_MAP)
 *    - Currencies in dollars (post-micro-normalisation)
 *    - Durations in milliseconds (preserved per Story 19.8 transformer)
 * 4. EXPECTED RECORD COUNTS: ~152 records per fixture (90 overview + 6 campaigns
 *    + 12 ad squads + 18 ads + 16 demographics + 10 country/OS).
 * 5. PER-CLIENT MAPPING RATIONALE: Snap's 90% Gen-Z penetration (per April 2026
 *    Snapchat for Business) aligns exclusively with TrendWear (DTC fashion /
 *    youth audience). FreshBrew Coffee, Summit Legal, CloudStack SaaS, and
 *    GreenLife Wellness don't fit Snap's young / aspirational / app-install-
 *    heavy ad-platform profile.
 *
 * Privacy: synthetic data only. No real customer IDs, no PII. landingPageUrl
 * values pass strict regex (no query strings, no fragments, HTTPS-only) — Round
 * 4 Red Team Attack 1 defence. customEventLabels content matches a strict
 * alphanumeric+safe-punctuation regex — Round 3 Red Team Attack 2 defence.
 *
 * Determinism: every random value flows through createRng(config.seed). Re-
 * running with the same seed yields byte-identical output (CI-gated per
 * Round 5 Reflection 6 / Pinterest 19.6 precedent).
 *
 * Source-of-truth references:
 *   - _bmad/bmm/data/integration-metrics/snapchat-ads.md — metric/dimension catalogue
 *   - _bmad/bmm/data/fixture-data-directive.md — realism rules + industry mapping
 *   - packages/integrations/src/snapchat/snapchat-ads.ts — adapter contract
 *   - packages/integrations/src/snapchat/transformers.ts — record-shape source-of-truth
 *
 * Known limitations:
 *   - Demo mode does NOT include creative-level / media-level breakdowns —
 *     those are async-report-only per Story 19.8 known limitations. If a
 *     future preset requires them, a follow-up story should land the live
 *     creative-list pre-fetch first.
 *   - SKAdNetwork (iOS attribution) is a separate report type per Story 19.8
 *     and not part of the 6-pipeline sync set. Out of 19.9 scope.
 */

import type { AdapterMetrics } from "../../adapter";
import type { FixtureConfig } from "../types";
import { createRng, randomFloat, randomInt } from "../rng";
import { generateDayMultipliers, applyMultiplier } from "../time-series";
import { safeDivide } from "../derived-metrics";

// ============================================================================
// Constants — exported & JSDoc'd for build-to-sell grep-ability (AC #18)
// ============================================================================

/**
 * Fixture schema version (Round 5 Reflection 7) — bump when the AdapterMetrics
 * shape changes. The adapter test logs a console.warn on mismatch so a stale
 * fixture surfaces during local dev without breaking CI.
 */
export const SNAPCHAT_ADS_FIXTURE_SCHEMA_VERSION = "19.9.0";

/**
 * The 6 sync `_reportType` discriminators. Mirrors Story 19.8's
 * `SNAPCHAT_ADS_REPORT_TYPES` tuple. Defined here as an independent constant so
 * the fixture generator does not import from the snapchat adapter package
 * (which would create a circular dependency between fixtures and adapters).
 * Round 5 Reflection 1 mitigation: fixture-validation tests assert set
 * equality between `new Set(records.map(r => r._reportType))` and this tuple.
 */
export const SNAPCHAT_ADS_REPORT_TYPES = [
  "overview",
  "campaigns",
  "adSquads",
  "ads",
  "ageGender",
  "countryOs",
] as const;

/**
 * Minimum threshold per metric to prevent zero values for "active" demo
 * campaigns. Picked to match the FDL realism rule "no zeros for active
 * campaigns" while staying in a believable Snap fashion-DTC range.
 *
 * Round 4 Red Team Attack 4 defence: validation tests assert every record's
 * spend / impressions / swipes meets the floor, catching any `correlatedChild`
 * clamp failure.
 */
export const SNAPCHAT_ADS_MIN_THRESHOLDS: Record<string, number> = {
  impressions: 1000,
  uniques: 500,
  swipes: 5,
  spend: 1,
  videoViews2s: 50,
  conversionPurchases: 0,
  totalInstalls: 0,
};

/**
 * Zipf-like budget distribution for the 6 Snap fashion DTC demo campaigns
 * (AC #5). Snap agencies typically run consolidated awareness + conversion +
 * app-install campaigns (similar to Pinterest's many-fewer-larger pattern).
 * Sum: 1.00 (4-decimal-rounded; small float drift normalised by the
 * generator's noise + re-normalisation pass).
 */
export const SNAPCHAT_ADS_CAMPAIGN_BUDGET_SHARES: ReadonlyArray<number> = [
  0.35, 0.24, 0.17, 0.12, 0.08, 0.04,
];

/**
 * The 6 demo campaign templates for TrendWear Fashion (AC #5). Each template
 * carries the campaign objective + bid/optimisation defaults Snap's API
 * exposes per April 2026 Snap Marketing API research.
 *
 * Names are intentionally vertical-flavoured so a buyer's eng can grep
 * "Spring Drop 2026" and immediately understand which TrendWear archetype
 * uses this template.
 */
export const SNAPCHAT_ADS_CAMPAIGN_DEFS: ReadonlyArray<{
  readonly name: string;
  readonly status: "ACTIVE" | "PAUSED";
  readonly objective:
    | "WEB_CONVERSION"
    | "APP_INSTALL"
    | "BRAND_AWARENESS"
    | "CATALOG_SALES"
    | "VIDEO_VIEW"
    | "ENGAGEMENT";
}> = [
  { name: "Conversions - Spring Drop 2026", status: "ACTIVE", objective: "WEB_CONVERSION" },
  { name: "App Install - iOS + Android Promo", status: "ACTIVE", objective: "APP_INSTALL" },
  { name: "Awareness - Story Ads Hero", status: "ACTIVE", objective: "BRAND_AWARENESS" },
  { name: "Catalog Sales - Bestsellers Feed", status: "ACTIVE", objective: "CATALOG_SALES" },
  { name: "Video Views - 6s Brand Spot", status: "ACTIVE", objective: "VIDEO_VIEW" },
  { name: "Engagement - Lens / AR Filter", status: "PAUSED", objective: "ENGAGEMENT" },
];

/**
 * Snap creative type enum per April 2026 Marketing API documentation. A
 * creative type drives whether a creative carries video metrics (STORY,
 * COMMERCIAL, SNAP_AD with video) or not (COLLECTION, DYNAMIC, AR_LENS,
 * FILTER).
 */
export const SNAPCHAT_ADS_CREATIVE_TYPES = [
  "SNAP_AD",
  "STORY",
  "COLLECTION",
  "DYNAMIC",
  "COMMERCIAL",
  "AR_LENS",
  "FILTER",
] as const;

/**
 * Per-campaign Promoted Snap creative names + types (AC #7). 3 ads per
 * campaign × 6 campaigns = 18 creatives. At least 6 carry video creative
 * types (STORY / COMMERCIAL / SNAP_AD with video) so the Video Quartile
 * Funnel preset has data; at least 2 carry AR_LENS so demos showcase Snap's
 * unique AR-experience differentiator (per April 2026 Snapchat for Business
 * 2025 performance review — AR experiences drove 50%+ engagement uplift for
 * fashion brands).
 *
 * URL hygiene: every landingPageUrl matches SNAPCHAT_ADS_LANDING_URL_REGEX
 * (Round 4 Red Team Attack 1). Creative names match
 * SNAPCHAT_ADS_CREATIVE_NAME_REGEX (Round 4 Red Team Attack 5).
 */
export const SNAPCHAT_ADS_CREATIVE_NAMES_BY_CAMPAIGN: Readonly<
  Record<
    string,
    ReadonlyArray<{
      readonly name: string;
      readonly creativeType: (typeof SNAPCHAT_ADS_CREATIVE_TYPES)[number];
      readonly landingPath: string;
    }>
  >
> = {
  "Conversions - Spring Drop 2026": [
    { name: "Spring 2026 Floral Maxi Dress - Story Ad", creativeType: "STORY", landingPath: "/collections/spring-2026" },
    { name: "Date Night Earrings - Gold Hoops - Single Image", creativeType: "SNAP_AD", landingPath: "/products/gold-hoops" },
    { name: "Cottagecore Linen Dress Collection", creativeType: "COLLECTION", landingPath: "/collections/cottagecore" },
  ],
  "App Install - iOS + Android Promo": [
    { name: "Download TrendWear App - iOS Hero", creativeType: "SNAP_AD", landingPath: "/app/ios" },
    { name: "Get the App - Android 6s Spot", creativeType: "COMMERCIAL", landingPath: "/app/android" },
    { name: "Style on the Go - App Story", creativeType: "STORY", landingPath: "/app/launch" },
  ],
  "Awareness - Story Ads Hero": [
    { name: "Behind the Brand - Lookbook Story", creativeType: "STORY", landingPath: "/about/lookbook" },
    { name: "Spring Sustainable Fabrics", creativeType: "STORY", landingPath: "/about/sustainability" },
    { name: "Founder Story - From Sketch to Showroom", creativeType: "COMMERCIAL", landingPath: "/about/founder" },
  ],
  "Catalog Sales - Bestsellers Feed": [
    { name: "Bestsellers Carousel - Spring Picks", creativeType: "DYNAMIC", landingPath: "/collections/bestsellers" },
    { name: "Top Rated Dresses - Catalog Feed", creativeType: "DYNAMIC", landingPath: "/collections/top-rated" },
    { name: "New Arrivals - Daily Refresh", creativeType: "DYNAMIC", landingPath: "/collections/new-arrivals" },
  ],
  "Video Views - 6s Brand Spot": [
    { name: "Summer Sneakers Reveal - 6s Spot", creativeType: "COMMERCIAL", landingPath: "/products/summer-sneakers" },
    { name: "Date Night Looks - Vertical Video", creativeType: "STORY", landingPath: "/lookbooks/date-night" },
    { name: "TrendWear x Spring Color Palette", creativeType: "COMMERCIAL", landingPath: "/lookbooks/spring-palette" },
  ],
  "Engagement - Lens / AR Filter": [
    { name: "Try On Sunglasses - AR Lens", creativeType: "AR_LENS", landingPath: "/try-on/sunglasses" },
    { name: "Virtual Wardrobe - AR Lens Experience", creativeType: "AR_LENS", landingPath: "/try-on/wardrobe" },
    { name: "Spring Floral Filter - Branded Filter", creativeType: "FILTER", landingPath: "/try-on/spring-filter" },
  ],
};

/**
 * Female-skewed Gen-Z age × gender distribution for TrendWear (AC #8). Snap's
 * documented audience is 90% of 13-24 year olds globally per April 2026
 * Snapchat for Business research. Female-skewed for fashion verticals (~65%+
 * female impressions per Sprout Social).
 *
 * Includes the Feb 2025 `55+` bucket alongside the Snap Ads Manager 5-bucket
 * UI default (`18-20, 21-24, 25-29, 30-34, 35+`) — Round 2 Pre-mortem D
 * mitigation: ensures the orchestrator's mixed-bucket warning code path
 * fires in demo mode.
 *
 * Shares sum to ~1.0 — slight rounding drift accepted (validation test
 * tolerance: ±0.05).
 */
export const SNAPCHAT_ADS_AGE_GENDER_DISTRIBUTION: ReadonlyArray<{
  readonly ageBucket: "18-20" | "21-24" | "25-29" | "30-34" | "35+" | "55+" | "50+";
  readonly gender: "FEMALE" | "MALE" | "OTHER";
  readonly share: number;
}> = [
  // Female-skewed Gen-Z distribution (~64% female). Shares are tuned to sum
  // exactly to 1.0 (verified by SNAPCHAT_ADS_AGE_GENDER_DISTRIBUTION sum-check
  // test). The validation tolerance is ±0.05 for float drift, but we keep the
  // listed values exact so a future PR can spot drift with a strict check.
  { ageBucket: "18-20", gender: "FEMALE", share: 0.215 },
  { ageBucket: "21-24", gender: "FEMALE", share: 0.235 },
  { ageBucket: "25-29", gender: "FEMALE", share: 0.140 },
  { ageBucket: "30-34", gender: "FEMALE", share: 0.060 },
  { ageBucket: "35+", gender: "FEMALE", share: 0.020 },
  { ageBucket: "18-20", gender: "MALE", share: 0.090 },
  { ageBucket: "21-24", gender: "MALE", share: 0.100 },
  { ageBucket: "25-29", gender: "MALE", share: 0.050 },
  { ageBucket: "30-34", gender: "MALE", share: 0.030 },
  { ageBucket: "35+", gender: "MALE", share: 0.010 },
  { ageBucket: "18-20", gender: "OTHER", share: 0.010 },
  { ageBucket: "21-24", gender: "OTHER", share: 0.010 },
  { ageBucket: "25-29", gender: "OTHER", share: 0.010 },
  { ageBucket: "30-34", gender: "OTHER", share: 0.010 },
  { ageBucket: "35+", gender: "OTHER", share: 0.0025 },
  // Round 2 Pre-mortem D + Story 19.9 Task 2.19 — exercise BOTH the legacy
  // `50+` bucket AND the new Feb 2025 `55+` bucket so the orchestrator's
  // mixed-bucket detection at snapchat-ads.ts:1186-1192 fires when fixture
  // loads. Female 55+ has the highest non-zero engagement among 55+
  // demographics per April 2026 Snap research; the 50+ bucket represents
  // pre-Feb-2025 campaign data still surfaced when querying date ranges
  // spanning the migration window.
  { ageBucket: "50+", gender: "FEMALE", share: 0.0025 },
  { ageBucket: "55+", gender: "FEMALE", share: 0.005 },
];

/**
 * Geographic + OS distribution for TrendWear (AC #9). Top-5 country-tier-1
 * markets per April 2026 Snapchat for Business research, each split by iOS /
 * Android. Snap users over-index on iOS (~55%) vs the general population's
 * ~50/50 split per April 2026 Sprout Social.
 *
 * `Other` OS values excluded for fixture parsimony (Snap shows ≤1% Other in
 * real data per April 2026 Sprout Social).
 *
 * Shares sum to ~1.0 across all 10 rows.
 */
export const SNAPCHAT_ADS_COUNTRY_OS_DISTRIBUTION: ReadonlyArray<{
  readonly country: string;
  readonly os: "iOS" | "Android";
  readonly share: number;
}> = [
  { country: "US", os: "iOS", share: 0.30 },
  { country: "US", os: "Android", share: 0.22 },
  { country: "GB", os: "iOS", share: 0.08 },
  { country: "GB", os: "Android", share: 0.06 },
  { country: "CA", os: "iOS", share: 0.06 },
  { country: "CA", os: "Android", share: 0.04 },
  { country: "AU", os: "iOS", share: 0.06 },
  { country: "AU", os: "Android", share: 0.04 },
  { country: "DE", os: "iOS", share: 0.08 },
  { country: "DE", os: "Android", share: 0.06 },
];

/**
 * Custom event slot labels for TrendWear Fashion (AC #2 metadata). Snap
 * exposes 5 custom event slots × 3 variants (total/_app/_web) per
 * `transformers.ts` line 261. The orchestrator at `snapchat-ads.ts:1217`
 * reads these from the live ad-account metadata response; fixture mode
 * populates them from this constant so the contract stays intact.
 *
 * Round 3 Red Team Attack 2 defence: validation test asserts every value
 * matches SNAPCHAT_ADS_CREATIVE_NAME_REGEX (alphanumeric + safe punctuation
 * only — no `<script>` or HTML).
 */
export const SNAPCHAT_ADS_CUSTOM_EVENT_LABELS: Readonly<Record<string, string>> = {
  "1": "AddToWishlist",
  "2": "QuizComplete",
  "3": "VipUnlock",
  "4": "StylePoll",
  "5": "ReferralComplete",
};

/**
 * The 25 conversion event base names that match
 * `packages/integrations/src/snapchat/transformers.ts` `CONVERSION_EVENTS`
 * exactly. Used to enumerate the 75 conversion variants (25 events × 3
 * variants total/_app/_web) at fixture-build time.
 *
 * Round 3 Self-Consistency Validation: this list MUST stay in sync with the
 * transformer's CONVERSION_EVENTS array. Diverging would silently produce a
 * fixture that the metric-platform-map registry can't bind widgets to. We
 * intentionally duplicate the list here (rather than importing from
 * `snapchat/transformers`) to avoid a fixtures→adapter circular dependency;
 * the validation test at `validation.test.ts` cross-checks both arrays for
 * parity.
 *
 * Each entry: `{ camel, hasValue, isRate }`
 *  - `camel` is the camelCase suffix used after the `conversion` prefix
 *  - `hasValue` adds a Currency `*Value` variant + `*ValueApp` + `*ValueWeb`
 *    (only `purchases` has this per Snap docs)
 *  - `isRate` flags rate-percent fields (only `rate` — emits `conversionRate`)
 */
export const SNAPCHAT_ADS_CONVERSION_EVENT_VARIANTS: ReadonlyArray<{
  readonly camel: string;
  readonly hasValue: boolean;
  readonly isRate: boolean;
}> = [
  { camel: "PageViews", hasValue: false, isRate: false },
  { camel: "ViewContent", hasValue: false, isRate: false },
  { camel: "AddCart", hasValue: false, isRate: false },
  { camel: "StartCheckout", hasValue: false, isRate: false },
  { camel: "AddBilling", hasValue: false, isRate: false },
  { camel: "Purchases", hasValue: true, isRate: false },
  { camel: "SignUps", hasValue: false, isRate: false },
  { camel: "Logins", hasValue: false, isRate: false },
  { camel: "Saves", hasValue: false, isRate: false },
  { camel: "Searches", hasValue: false, isRate: false },
  { camel: "Shares", hasValue: false, isRate: false },
  { camel: "AppOpens", hasValue: false, isRate: false },
  { camel: "StartTrial", hasValue: false, isRate: false },
  { camel: "Subscribe", hasValue: false, isRate: false },
  { camel: "AddToWishlist", hasValue: false, isRate: false },
  { camel: "ListView", hasValue: false, isRate: false },
  { camel: "Reserve", hasValue: false, isRate: false },
  { camel: "LevelCompletes", hasValue: false, isRate: false },
  { camel: "CompleteTutorial", hasValue: false, isRate: false },
  { camel: "AchievementUnlocked", hasValue: false, isRate: false },
  { camel: "SpendCredits", hasValue: false, isRate: false },
  { camel: "Invite", hasValue: false, isRate: false },
  { camel: "AdClick", hasValue: false, isRate: false },
  { camel: "AdView", hasValue: false, isRate: false },
  { camel: "Rate", hasValue: false, isRate: true },
];

/**
 * URL hygiene regex for landingPageUrl values in `_reportType: "ads"` records
 * (Round 4 Red Team Attack 1). Allows: HTTPS scheme, lowercase host, optional
 * lowercase path with letters/digits/hyphens/slashes, optional trailing
 * slash. Rejects: query strings, fragments, uppercase, non-https schemes
 * (`javascript:`, `data:`, `file:`).
 */
export const SNAPCHAT_ADS_LANDING_URL_REGEX = /^https:\/\/[a-z0-9.-]+\.[a-z]{2,}(\/[a-z0-9/-]*)?\/?$/;

/**
 * Creative + custom-event-label content regex (Round 4 Red Team Attack 5,
 * Round 3 Red Team Attack 2). Alphanumeric + safe punctuation only —
 * defence-in-depth alongside React's auto-escaping.
 */
export const SNAPCHAT_ADS_CREATIVE_NAME_REGEX = /^[A-Za-z0-9 \-,.&'!]+$/;

/**
 * Snap's documented bid strategy enum (per April 2026 Marketing API research).
 * Used at the ad-squad level to pick a deterministic per-row value.
 */
export const SNAPCHAT_ADS_BID_STRATEGIES = [
  "LOWEST_COST_WITH_MAX_BID",
  "TARGET_COST",
  "AUTO_BID",
] as const;

/**
 * Snap's documented optimization-goal enum (per April 2026 Marketing API
 * research). Used at the ad-squad level to pick a deterministic per-row
 * value.
 */
export const SNAPCHAT_ADS_OPTIMIZATION_GOALS = [
  "IMPRESSIONS",
  "SWIPES",
  "INSTALLS",
  "PIXEL_PURCHASE",
  "PIXEL_ADD_CART",
  "PIXEL_PAGE_VIEW",
  "VIDEO_VIEWS_2S",
  "VIDEO_VIEWS_15S",
  "USES",
  "REACH",
] as const;

/**
 * Demo landing page domain for TrendWear (synthetic — not a real domain).
 */
const TRENDWEAR_DOMAIN = "https://trendwear.com";

/**
 * App-attribution share ranges for Snap conversion events. Snap's iOS-skew
 * (Gen-Z over-indexes iOS per April 2026 Sprout Social) drives App share to
 * ~55% for fashion DTC. Each event has a slightly different range to model
 * realistic per-event variance — purchase events lean app-heavier than page
 * views (deeper-funnel intent on mobile). All values are App share; Web =
 * 1 - App.
 */
export const SNAPCHAT_ADS_APP_SHARE_RANGES: Readonly<{
  readonly purchase: readonly [number, number];
  readonly pageView: readonly [number, number];
  readonly addCart: readonly [number, number];
  readonly signUp: readonly [number, number];
  readonly install: readonly [number, number];
  readonly customEvent: readonly [number, number];
  readonly iosOfTotal: readonly [number, number];
}> = {
  purchase: [0.50, 0.62],
  pageView: [0.55, 0.65],
  addCart: [0.50, 0.62],
  signUp: [0.55, 0.65],
  install: [0.55, 0.70],
  customEvent: [0.55, 0.65],
  iosOfTotal: [0.55, 0.65],
};

// ============================================================================
// Helpers
// ============================================================================

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function roundRate(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/**
 * Deterministic UUID derived from a string. Snap's real campaign / ad squad /
 * ad / creative IDs are 36-char UUIDs with 4 hyphens (8-4-4-4-12). This
 * helper produces a UUID-shaped value seeded from the input string — same
 * input always yields the same UUID, so re-runs of the generator are stable.
 *
 * The output is NOT a cryptographically valid UUID (no version bits) but
 * matches Story 19.8's UUID regex `/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i`.
 */
function deterministicUuid(input: string): string {
  let h1 = 5381;
  let h2 = 5381;
  for (let i = 0; i < input.length; i++) {
    h1 = ((h1 << 5) + h1 + input.charCodeAt(i)) | 0;
  }
  for (let i = input.length - 1; i >= 0; i--) {
    h2 = ((h2 << 5) + h2 + input.charCodeAt(i)) | 0;
  }
  // Build a 32-char hex body from the two djb2 hashes (each pads to 8 chars).
  const a = (Math.abs(h1) >>> 0).toString(16).padStart(8, "0");
  const b = (Math.abs(h2) >>> 0).toString(16).padStart(8, "0");
  // Repeat to fill 32 chars deterministically.
  const body32 = `${a}${b}${a}${b}`.slice(0, 32);
  return `${body32.slice(0, 8)}-${body32.slice(8, 12)}-${body32.slice(12, 16)}-${body32.slice(16, 20)}-${body32.slice(20, 32)}`;
}

function campaignUuid(name: string): string {
  return deterministicUuid(`campaign:${name}`);
}

function adSquadUuid(parentCampaign: string, suffix: string): string {
  return deterministicUuid(`adsquad:${parentCampaign}::${suffix}`);
}

function adUuid(creativeName: string): string {
  return deterministicUuid(`ad:${creativeName}`);
}

function creativeUuid(creativeName: string): string {
  return deterministicUuid(`creative:${creativeName}`);
}

/**
 * Build the conversion-event variant keyset for a single record. Returns
 * 75 conversion-* keys (25 events × 3 variants total/App/Web) plus the 3
 * purchase-value variants plus the 3 rate variants — exactly mirrors the
 * Story 19.8 transformer output.
 */
function buildConversionMetricKeys(): ReadonlyArray<{
  readonly key: string;
  readonly isRate: boolean;
  readonly isCurrency: boolean;
}> {
  const keys: Array<{ key: string; isRate: boolean; isCurrency: boolean }> = [];
  for (const ev of SNAPCHAT_ADS_CONVERSION_EVENT_VARIANTS) {
    const base = `conversion${ev.camel}`;
    keys.push({ key: base, isRate: ev.isRate, isCurrency: false });
    keys.push({ key: `${base}App`, isRate: ev.isRate, isCurrency: false });
    keys.push({ key: `${base}Web`, isRate: ev.isRate, isCurrency: false });
    if (ev.hasValue) {
      keys.push({ key: `${base}Value`, isRate: false, isCurrency: true });
      keys.push({ key: `${base}ValueApp`, isRate: false, isCurrency: true });
      keys.push({ key: `${base}ValueWeb`, isRate: false, isCurrency: true });
    }
  }
  return keys;
}

const CONVERSION_METRIC_KEYS = buildConversionMetricKeys();

// ============================================================================
// Generator
// ============================================================================

export function generateSnapchatAdsFixtures(
  config: FixtureConfig
): AdapterMetrics {
  const rng = createRng(config.seed);
  const base = config.baseMetrics;
  const slug = config.clientSlug;

  // Snapchat fixtures only ship for TrendWear in Story 19.9 (AD-19.9-02).
  // Throw loudly on unknown slugs — silent fallback would mislead reviewers.
  if (slug !== "trendwear-fashion") {
    throw new Error(
      `Snapchat Ads fixture generator only supports clientSlug="trendwear-fashion" (got "${slug}"). ` +
        `Per AD-19.9-02, Snap's documented 90% Gen-Z audience aligns ` +
        `exclusively with TrendWear (DTC fashion / Gen-Z) in our 5-client ` +
        `roster. To add a second client, extend SNAPCHAT_ADS_CAMPAIGN_DEFS / ` +
        `SNAPCHAT_ADS_CREATIVE_NAMES_BY_CAMPAIGN with that client's archetype ` +
        `before re-running the generator.`
    );
  }

  // Require baseMetrics: silent fallback would ship default values labeled
  // with a different client's name, hiding misconfiguration. Story Task 2.1
  // mandates the 28-field baseMetrics object — we spot-check the load-bearing
  // ones here.
  for (const required of [
    "impressions",
    "uniques",
    "spend",
    "swipes",
    "videoViews2s",
    "conversionPurchases",
    "conversionPurchasesValue",
    "totalInstalls",
  ] as const) {
    if (typeof base[required] !== "number" || !Number.isFinite(base[required])) {
      throw new Error(
        `Snapchat Ads fixture generator requires config.baseMetrics["${required}"] ` +
          `(got ${String(base[required])}) for client "${slug}". Add it to ` +
          `DEMO_CLIENTS["${slug}"].baseMetrics["snapchat-ads"] in demo-accounts.ts.`
      );
    }
  }

  const startDate = config.startDate;
  const endDate = config.endDate;
  const days =
    Math.round(
      (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)
    ) + 1;

  const dayMultipliers = generateDayMultipliers(
    {
      days,
      startDate,
      monthlyGrowth: randomFloat(rng, 0.02, 0.05),
      weekendBehavior: config.weekendBehavior,
      noiseRange: randomFloat(rng, 0.10, 0.18),
      spikeCount: randomInt(rng, 2, 3),
      spikeMultiplier: [1.3, 1.8],
    },
    rng
  );

  const records: Record<string, unknown>[] = [];
  const warnings: Array<{ reportType: string; reason: string }> = [];

  // Account context constants. The UUID matches Story 19.8's example value
  // and AC #1 — fixture-mode short-circuit at snapchat-ads.ts:931-941 fires
  // before any HTTP request, so this value never reaches the API.
  const accountId = config.accountId;
  const currencyCode = "USD";
  const timeZone = "America/Los_Angeles";

  // Attribution windows — Snap's documented defaults per April 2026 research
  // (verified against developers.snap.com/api/marketing-api/Ads-API/measurement).
  const swipeUpAttributionWindow = "28_DAY";
  const viewAttributionWindow = "1_DAY";
  const conversionSourceTypes = "total";
  const actionReportTime = "conversion";

  // Snap docs: "Metrics are finalized 48 hours after the end of the day in
  // the timezone set by the Ad Account." Hoisted out of the per-day loop —
  // identical across all 90 overview rows.
  const finalizedDataEndTimeIso = new Date(
    config.endDate.getTime() - 48 * 60 * 60 * 1000
  ).toISOString();

  // Accumulators for the campaign cross-check (AC #5 ±0.5%).
  let totalImpressionsAcc = 0;
  let totalUniquesAcc = 0;
  let totalSpendAcc = 0;
  let totalSwipesAcc = 0;
  let totalScreenTimeMillisAcc = 0;
  let totalViewTimeMillisAcc = 0;
  let totalVideoViews2sAcc = 0;
  let totalVideoViews15sAcc = 0;
  let totalViewCompletionAcc = 0;
  let totalVideoQuartile1Acc = 0;
  let totalVideoQuartile2Acc = 0;
  let totalVideoQuartile3Acc = 0;
  let totalInstallsAcc = 0;
  let totalInstallsAppAcc = 0;
  let totalInstallsWebAcc = 0;
  let totalConversionPurchasesAcc = 0;
  let totalConversionPurchasesAppAcc = 0;
  let totalConversionPurchasesWebAcc = 0;
  let totalConversionPurchasesValueAcc = 0;
  let totalConversionPurchasesValueAppAcc = 0;
  let totalConversionPurchasesValueWebAcc = 0;
  let totalConversionPageViewsAcc = 0;
  let totalConversionAddCartAcc = 0;
  let totalConversionSignUpsAcc = 0;

  // ============ 1. Overview (90 daily rows) =================================
  for (const day of dayMultipliers) {
    const impressions = applyMultiplier(
      base.impressions ?? 25000,
      day.multiplier,
      SNAPCHAT_ADS_MIN_THRESHOLDS.impressions!
    );
    const uniques = applyMultiplier(
      base.uniques ?? 14000,
      day.multiplier,
      SNAPCHAT_ADS_MIN_THRESHOLDS.uniques!
    );
    const frequency = roundRate(safeDivide(impressions, uniques));
    const screenTimeMillis = applyMultiplier(
      base.screenTimeMillis ?? 4_125_000,
      day.multiplier,
      0
    );
    const avgScreenTimeMillis = Math.round(safeDivide(screenTimeMillis, uniques));
    const spendRaw = (base.spend ?? 145) * day.multiplier;
    const spend = roundCurrency(
      Math.max(SNAPCHAT_ADS_MIN_THRESHOLDS.spend!, spendRaw)
    );

    // Engagement — swipe-up rate ≈ swipes / impressions ± 0.0001 per row
    // (computed bottom-up to enforce the invariant).
    const swipes = applyMultiplier(
      base.swipes ?? 575,
      day.multiplier,
      SNAPCHAT_ADS_MIN_THRESHOLDS.swipes!
    );
    const swipeUpRate = roundRate(safeDivide(swipes, impressions));
    const viewTimeMillis = applyMultiplier(
      base.viewTimeMillis ?? 2_750_000,
      day.multiplier,
      0
    );

    // Video — bottom-up monotonicity per Round 4 Red Team Attack 5:
    // videoViews2s ≥ videoQuartile1 ≥ videoQuartile2 ≥ videoQuartile3 ≥ viewCompletion.
    //
    // NOTE: `base.videoViews15s` and `base.viewCompletion` ARE provided by
    // demo-accounts.ts (per AC #3) but we deliberately compute them
    // bottom-up from `videoViews2s` to enforce the monotonicity invariant
    // — independently amplifying each from baseMetrics would risk negative
    // drop-off (q2 > q1 etc.). The base values document the fashion DTC
    // funnel shape; the generator preserves the relative scale.
    const videoViews2s = applyMultiplier(
      base.videoViews2s ?? 9500,
      day.multiplier,
      SNAPCHAT_ADS_MIN_THRESHOLDS.videoViews2s!
    );
    const videoViews15s = Math.round(
      videoViews2s * randomFloat(rng, 0.20, 0.28)
    );
    const videoViewsTimeBased = Math.round(
      videoViews2s * randomFloat(rng, 0.85, 1.05)
    );
    const videoQuartile1 = Math.round(
      videoViews2s * randomFloat(rng, 0.40, 0.45)
    );
    const videoQuartile2 = Math.round(
      videoQuartile1 * randomFloat(rng, 0.62, 0.70)
    );
    const videoQuartile3 = Math.round(
      videoQuartile2 * randomFloat(rng, 0.62, 0.70)
    );
    const viewCompletion = Math.min(
      videoQuartile3,
      Math.round(videoQuartile3 * randomFloat(rng, 0.50, 0.65))
    );

    // Attachment video metrics — proportional to main video, smaller scale.
    const attachmentImpressions = Math.round(impressions * randomFloat(rng, 0.30, 0.42));
    const attachmentUniques = Math.round(attachmentImpressions / randomFloat(rng, 1.5, 2.0));
    const attachmentFrequency = roundRate(
      safeDivide(attachmentImpressions, attachmentUniques)
    );
    const attachmentTotalViewTimeMillis = Math.round(
      viewTimeMillis * randomFloat(rng, 0.55, 0.70)
    );
    const attachmentAvgViewTimeMillis = Math.round(
      safeDivide(attachmentTotalViewTimeMillis, attachmentUniques)
    );
    const attachmentViewCompletion = Math.round(
      attachmentImpressions * randomFloat(rng, 0.10, 0.16)
    );
    const attachmentQuartile1 = Math.round(
      attachmentImpressions * randomFloat(rng, 0.42, 0.48)
    );
    const attachmentQuartile2 = Math.round(
      attachmentQuartile1 * randomFloat(rng, 0.62, 0.70)
    );
    const attachmentQuartile3 = Math.round(
      attachmentQuartile2 * randomFloat(rng, 0.62, 0.70)
    );

    // App installs — App + Web split sums to total invariant (Round 2 C).
    const totalInstalls = applyMultiplier(
      base.totalInstalls ?? 35,
      day.multiplier,
      SNAPCHAT_ADS_MIN_THRESHOLDS.totalInstalls!
    );
    const totalInstallsAppRatio = randomFloat(rng, 0.55, 0.70);
    const totalInstallsApp = Math.round(totalInstalls * totalInstallsAppRatio);
    const totalInstallsWeb = Math.max(0, totalInstalls - totalInstallsApp);
    const iosInstallsRatio = randomFloat(rng, 0.55, 0.65);
    const iosInstalls = Math.round(totalInstalls * iosInstallsRatio);
    const iosInstallsApp = Math.round(totalInstallsApp * iosInstallsRatio);
    const iosInstallsWeb = Math.max(0, iosInstalls - iosInstallsApp);
    const androidInstalls = Math.max(0, totalInstalls - iosInstalls);
    const androidInstallsApp = Math.max(0, totalInstallsApp - iosInstallsApp);
    const androidInstallsWeb = Math.max(0, androidInstalls - androidInstallsApp);

    // Conversions — Purchases (AC #4) — App + Web sum invariant. Currency
    // values in DOLLARS (post-micro-normalisation per Story 19.8 transformer).
    const conversionPurchases = applyMultiplier(
      base.conversionPurchases ?? 14,
      day.multiplier,
      0
    );
    const conversionPurchasesAppRatio = randomFloat(rng, 0.50, 0.62);
    const conversionPurchasesApp = Math.round(
      conversionPurchases * conversionPurchasesAppRatio
    );
    const conversionPurchasesWeb = Math.max(
      0,
      conversionPurchases - conversionPurchasesApp
    );

    const conversionPurchasesValue = roundCurrency(
      (base.conversionPurchasesValue ?? 770) * day.multiplier
    );
    const conversionPurchasesValueApp = roundCurrency(
      conversionPurchasesValue * conversionPurchasesAppRatio
    );
    const conversionPurchasesValueWeb = roundCurrency(
      Math.max(0, conversionPurchasesValue - conversionPurchasesValueApp)
    );

    // Other top conversion events with App+Web split invariant.
    const conversionPageViews = applyMultiplier(
      base.conversionPageViews ?? 850,
      day.multiplier,
      0
    );
    const conversionPageViewsAppRatio = randomFloat(rng, 0.55, 0.65);
    const conversionPageViewsApp = Math.round(
      conversionPageViews * conversionPageViewsAppRatio
    );
    const conversionPageViewsWeb = Math.max(
      0,
      conversionPageViews - conversionPageViewsApp
    );

    const conversionAddCart = applyMultiplier(
      base.conversionAddCart ?? 165,
      day.multiplier,
      0
    );
    const conversionAddCartAppRatio = randomFloat(rng, 0.50, 0.62);
    const conversionAddCartApp = Math.round(
      conversionAddCart * conversionAddCartAppRatio
    );
    const conversionAddCartWeb = Math.max(
      0,
      conversionAddCart - conversionAddCartApp
    );

    const conversionSignUps = applyMultiplier(
      base.conversionSignUps ?? 45,
      day.multiplier,
      0
    );
    const conversionSignUpsAppRatio = randomFloat(rng, 0.55, 0.65);
    const conversionSignUpsApp = Math.round(
      conversionSignUps * conversionSignUpsAppRatio
    );
    const conversionSignUpsWeb = Math.max(
      0,
      conversionSignUps - conversionSignUpsApp
    );

    // Long-tail conversion events — smaller scale, similar split logic.
    const longTailEvents = [
      { key: "conversionViewContent", baseValue: base.conversionViewContent ?? 410, appRatio: randomFloat(rng, 0.50, 0.62) },
      { key: "conversionStartCheckout", baseValue: base.conversionStartCheckout ?? 95, appRatio: randomFloat(rng, 0.50, 0.62) },
      { key: "conversionAppOpens", baseValue: base.conversionAppOpens ?? 220, appRatio: randomFloat(rng, 0.85, 0.95) },
      { key: "conversionSaves", baseValue: base.conversionSaves ?? 78, appRatio: randomFloat(rng, 0.55, 0.65) },
      { key: "conversionShares", baseValue: base.conversionShares ?? 24, appRatio: randomFloat(rng, 0.55, 0.65) },
      { key: "conversionLogins", baseValue: base.conversionLogins ?? 32, appRatio: randomFloat(rng, 0.60, 0.70) },
      { key: "conversionSearches", baseValue: base.conversionSearches ?? 51, appRatio: randomFloat(rng, 0.50, 0.60) },
    ];
    const longTailValues: Record<string, number> = {};
    for (const ev of longTailEvents) {
      const total = applyMultiplier(ev.baseValue, day.multiplier, 0);
      const app = Math.round(total * ev.appRatio);
      const web = Math.max(0, total - app);
      longTailValues[ev.key] = total;
      longTailValues[`${ev.key}App`] = app;
      longTailValues[`${ev.key}Web`] = web;
    }

    // Conversion rate (single fraction in [0,1]).
    const conversionRate = roundRate(safeDivide(conversionPurchases, swipes));
    const conversionRateApp = roundRate(safeDivide(conversionPurchasesApp, swipes));
    const conversionRateWeb = roundRate(safeDivide(conversionPurchasesWeb, swipes));

    // Long-tail conversion events not separately configured in baseMetrics —
    // emit them with the App+Web sum invariant intact (Round 4 Red Team Attack
    // 4 / Pre-mortem C). Each unmodeled event gets:
    //   - a small total ~5-15% of conversionPurchases scale
    //   - App share derived from the SAME `conversionPurchasesAppRatio` used
    //     above so the iOS-skew is consistent across all events for this row
    //   - Web = total - App (preserves App + Web === total exactly)
    //   - rate fields zeroed (no derived rates for long-tail events)
    //   - currency fields zeroed (only Purchases has a value variant per
    //     SNAPCHAT_ADS_CONVERSION_EVENT_VARIANTS)
    // This satisfies the Round 4 Attack invariant for ALL 25 events, not just
    // the top 4 explicitly tested in validation.test.ts.
    const conversionEventLongTail: Record<string, number> = {};
    const trackedConversionKeys = new Set([
      "conversionPurchases",
      "conversionPurchasesApp",
      "conversionPurchasesWeb",
      "conversionPurchasesValue",
      "conversionPurchasesValueApp",
      "conversionPurchasesValueWeb",
      "conversionPageViews",
      "conversionPageViewsApp",
      "conversionPageViewsWeb",
      "conversionAddCart",
      "conversionAddCartApp",
      "conversionAddCartWeb",
      "conversionSignUps",
      "conversionSignUpsApp",
      "conversionSignUpsWeb",
      "conversionRate",
      "conversionRateApp",
      "conversionRateWeb",
      ...Object.keys(longTailValues),
    ]);
    // Walk the keyset in stride-3 (total → App → Web), computing totals once
    // and deriving the splits, so the App+Web === total invariant holds for
    // every long-tail event without independent rolls of randomFloat.
    for (let i = 0; i < CONVERSION_METRIC_KEYS.length; i++) {
      const k = CONVERSION_METRIC_KEYS[i]!;
      if (trackedConversionKeys.has(k.key)) continue;
      if (k.isRate) {
        conversionEventLongTail[k.key] = 0;
        continue;
      }
      if (k.isCurrency) {
        conversionEventLongTail[k.key] = 0;
        continue;
      }
      // Total event — generate; the next two iterations will be App / Web.
      if (!k.key.endsWith("App") && !k.key.endsWith("Web")) {
        const total = Math.max(
          0,
          Math.round(conversionPurchases * randomFloat(rng, 0.05, 0.15))
        );
        conversionEventLongTail[k.key] = total;
        // Derive App/Web from the parent total so the invariant holds.
        const app = Math.round(total * conversionPurchasesAppRatio);
        const web = Math.max(0, total - app);
        conversionEventLongTail[`${k.key}App`] = app;
        conversionEventLongTail[`${k.key}Web`] = web;
      }
      // App / Web entries already populated by the parent's branch above.
      // Skip — but if a stray case where the parent isn't visited first
      // happens, fall through to 0 to keep the schema complete.
      if (
        (k.key.endsWith("App") || k.key.endsWith("Web")) &&
        conversionEventLongTail[k.key] === undefined
      ) {
        conversionEventLongTail[k.key] = 0;
      }
    }

    // Custom events — 5 slots × 3 variants. Scale ~10-30% of
    // conversionPurchases for realistic activity.
    const customEventValues: Record<string, number> = {};
    for (let slot = 1; slot <= 5; slot++) {
      const total = Math.max(
        0,
        Math.round(conversionPurchases * randomFloat(rng, 0.10, 0.30))
      );
      const appRatio = randomFloat(rng, 0.55, 0.65);
      const app = Math.round(total * appRatio);
      const web = Math.max(0, total - app);
      customEventValues[`customEvent${slot}`] = total;
      customEventValues[`customEvent${slot}App`] = app;
      customEventValues[`customEvent${slot}Web`] = web;
    }

    // Update accumulators (volume metrics only — derived metrics like
    // swipeUpRate / conversionRate are computed independently per row and
    // are NOT cross-checked at the campaign-level rollup).
    totalImpressionsAcc += impressions;
    totalUniquesAcc += uniques;
    totalSpendAcc = roundCurrency(totalSpendAcc + spend);
    totalSwipesAcc += swipes;
    totalScreenTimeMillisAcc += screenTimeMillis;
    totalViewTimeMillisAcc += viewTimeMillis;
    totalVideoViews2sAcc += videoViews2s;
    totalVideoViews15sAcc += videoViews15s;
    totalViewCompletionAcc += viewCompletion;
    totalVideoQuartile1Acc += videoQuartile1;
    totalVideoQuartile2Acc += videoQuartile2;
    totalVideoQuartile3Acc += videoQuartile3;
    totalInstallsAcc += totalInstalls;
    totalInstallsAppAcc += totalInstallsApp;
    totalInstallsWebAcc += totalInstallsWeb;
    totalConversionPurchasesAcc += conversionPurchases;
    totalConversionPurchasesAppAcc += conversionPurchasesApp;
    totalConversionPurchasesWebAcc += conversionPurchasesWeb;
    totalConversionPurchasesValueAcc = roundCurrency(
      totalConversionPurchasesValueAcc + conversionPurchasesValue
    );
    totalConversionPurchasesValueAppAcc = roundCurrency(
      totalConversionPurchasesValueAppAcc + conversionPurchasesValueApp
    );
    totalConversionPurchasesValueWebAcc = roundCurrency(
      totalConversionPurchasesValueWebAcc + conversionPurchasesValueWeb
    );
    totalConversionPageViewsAcc += conversionPageViews;
    totalConversionAddCartAcc += conversionAddCart;
    totalConversionSignUpsAcc += conversionSignUps;

    records.push({
      _reportType: "overview",
      date: day.dateStr,
      _entityId: accountId,
      _entityType: "AD_ACCOUNT",
      _finalizedDataEndTime: finalizedDataEndTimeIso,

      // Delivery
      impressions,
      uniques,
      frequency,
      screenTimeMillis,
      avgScreenTimeMillis,
      spend,

      // Engagement
      swipeUps: swipes,
      swipeUpRate,
      viewTimeMillis,

      // Video — Main
      videoViews2s,
      videoViews15s,
      videoViewsTimeBased,
      viewCompletion,
      videoQuartile1,
      videoQuartile2,
      videoQuartile3,

      // Video — Attachment
      attachmentImpressions,
      attachmentUniques,
      attachmentFrequency,
      attachmentAvgViewTimeMillis,
      attachmentTotalViewTimeMillis,
      attachmentViewCompletion,
      attachmentQuartile1,
      attachmentQuartile2,
      attachmentQuartile3,

      // App Installs
      totalInstalls,
      totalInstallsApp,
      totalInstallsWeb,
      iosInstalls,
      iosInstallsApp,
      iosInstallsWeb,
      androidInstalls,
      androidInstallsApp,
      androidInstallsWeb,

      // Tracked conversions (top events with explicit App/Web split)
      conversionPurchases,
      conversionPurchasesApp,
      conversionPurchasesWeb,
      conversionPurchasesValue,
      conversionPurchasesValueApp,
      conversionPurchasesValueWeb,
      conversionPageViews,
      conversionPageViewsApp,
      conversionPageViewsWeb,
      conversionAddCart,
      conversionAddCartApp,
      conversionAddCartWeb,
      conversionSignUps,
      conversionSignUpsApp,
      conversionSignUpsWeb,

      // Conversion rate
      conversionRate,
      conversionRateApp,
      conversionRateWeb,

      // Long-tail conversion events
      ...longTailValues,

      // Conversion zerofill (remaining 75-keyset coverage)
      // Long-tail conversion events with App+Web sum invariant (Round 4 Attack 4)
      ...conversionEventLongTail,

      // Custom events 1..5 (×3 variants = 15 keys)
      ...customEventValues,
    });
  }

  // ============ 2. Campaigns (6 rows) ======================================
  // Add ±15% noise to budget shares then re-normalize so they sum to 1.0.
  const noisyShares = SNAPCHAT_ADS_CAMPAIGN_BUDGET_SHARES.map(
    (s) => s * randomFloat(rng, 0.85, 1.15)
  );
  const shareSum = noisyShares.reduce((a, b) => a + b, 0);
  const normalizedShares = noisyShares.map((s) => s / shareSum);

  for (let i = 0; i < SNAPCHAT_ADS_CAMPAIGN_DEFS.length; i++) {
    const camp = SNAPCHAT_ADS_CAMPAIGN_DEFS[i]!;
    const share = normalizedShares[i]!;
    const cId = campaignUuid(camp.name);

    const campImpressions = Math.round(totalImpressionsAcc * share);
    const campUniques = Math.round(totalUniquesAcc * share);
    const campFrequency = roundRate(safeDivide(campImpressions, campUniques));
    const campSpend = roundCurrency(totalSpendAcc * share);
    const campSwipes = Math.round(totalSwipesAcc * share);
    const campSwipeUpRate = roundRate(safeDivide(campSwipes, campImpressions));
    const campScreenTimeMillis = Math.round(totalScreenTimeMillisAcc * share);
    const campViewTimeMillis = Math.round(totalViewTimeMillisAcc * share);
    const campAvgScreenTimeMillis = Math.round(
      safeDivide(campScreenTimeMillis, campUniques)
    );

    const campVideoViews2s = Math.round(totalVideoViews2sAcc * share);
    const campVideoViews15s = Math.round(totalVideoViews15sAcc * share);
    const campViewCompletion = Math.round(totalViewCompletionAcc * share);
    const campVideoQuartile1 = Math.round(totalVideoQuartile1Acc * share);
    const campVideoQuartile2 = Math.round(totalVideoQuartile2Acc * share);
    const campVideoQuartile3 = Math.round(totalVideoQuartile3Acc * share);

    const campTotalInstalls = Math.round(totalInstallsAcc * share);
    const campTotalInstallsApp = Math.round(totalInstallsAppAcc * share);
    const campTotalInstallsWeb = Math.max(
      0,
      campTotalInstalls - campTotalInstallsApp
    );

    const campConversionPurchases = Math.round(
      totalConversionPurchasesAcc * share
    );
    const campConversionPurchasesApp = Math.round(
      totalConversionPurchasesAppAcc * share
    );
    const campConversionPurchasesWeb = Math.max(
      0,
      campConversionPurchases - campConversionPurchasesApp
    );
    const campConversionPurchasesValue = roundCurrency(
      totalConversionPurchasesValueAcc * share
    );
    const campConversionPurchasesValueApp = roundCurrency(
      totalConversionPurchasesValueAppAcc * share
    );
    const campConversionPurchasesValueWeb = roundCurrency(
      Math.max(
        0,
        campConversionPurchasesValue - campConversionPurchasesValueApp
      )
    );

    const campConversionPageViews = Math.round(
      totalConversionPageViewsAcc * share
    );
    const campConversionAddCart = Math.round(totalConversionAddCartAcc * share);
    const campConversionSignUps = Math.round(totalConversionSignUpsAcc * share);
    const campConversionRate = roundRate(
      safeDivide(campConversionPurchases, campSwipes)
    );

    const dailyBudget = roundCurrency(campSpend / Math.max(1, days));

    records.push({
      _reportType: "campaigns",
      date: dayMultipliers[0]!.dateStr,
      _entityId: cId,
      _entityType: "CAMPAIGN",
      campaignId: cId,
      campaignName: camp.name,
      campaignStatus: camp.status,
      campaignObjective: camp.objective,
      dailyBudget,
      // Snap attribution-window stamps per AC #5.
      swipeUpAttributionWindow,
      viewAttributionWindow,
      conversionSourceTypes,
      actionReportTime,

      // Delivery
      impressions: campImpressions,
      uniques: campUniques,
      frequency: campFrequency,
      screenTimeMillis: campScreenTimeMillis,
      avgScreenTimeMillis: campAvgScreenTimeMillis,
      spend: campSpend,

      // Engagement
      swipeUps: campSwipes,
      swipeUpRate: campSwipeUpRate,
      viewTimeMillis: campViewTimeMillis,

      // Video — Main
      videoViews2s: campVideoViews2s,
      videoViews15s: campVideoViews15s,
      viewCompletion: campViewCompletion,
      videoQuartile1: campVideoQuartile1,
      videoQuartile2: campVideoQuartile2,
      videoQuartile3: campVideoQuartile3,

      // App Installs
      totalInstalls: campTotalInstalls,
      totalInstallsApp: campTotalInstallsApp,
      totalInstallsWeb: campTotalInstallsWeb,

      // Conversions (top events)
      conversionPurchases: campConversionPurchases,
      conversionPurchasesApp: campConversionPurchasesApp,
      conversionPurchasesWeb: campConversionPurchasesWeb,
      conversionPurchasesValue: campConversionPurchasesValue,
      conversionPurchasesValueApp: campConversionPurchasesValueApp,
      conversionPurchasesValueWeb: campConversionPurchasesValueWeb,
      conversionPageViews: campConversionPageViews,
      conversionAddCart: campConversionAddCart,
      conversionSignUps: campConversionSignUps,
      conversionRate: campConversionRate,
    });
  }

  // ============ 3. Ad Squads (12 rows = 2 per campaign) ====================
  // Snap vocabulary — NOT "ad groups" / "ad sets". Each parent campaign
  // gets 2 ad squads (60% / 40% of campaign volume).
  const adSquadSuffixes: ReadonlyArray<{
    readonly suffix: string;
    readonly bidStrategyIndex: number;
    readonly optimizationGoalIndex: number;
  }> = [
    { suffix: "Lookalike: Past Buyers (1%)", bidStrategyIndex: 0, optimizationGoalIndex: 3 }, // PIXEL_PURCHASE
    { suffix: "Interest: Fashion + Beauty (18-34)", bidStrategyIndex: 2, optimizationGoalIndex: 1 }, // SWIPES
    { suffix: "Audience: Email Subscribers", bidStrategyIndex: 1, optimizationGoalIndex: 4 }, // PIXEL_ADD_CART
    { suffix: "Lookalike: Cart Abandoners (3%)", bidStrategyIndex: 0, optimizationGoalIndex: 5 }, // PIXEL_PAGE_VIEW
  ];

  for (let i = 0; i < SNAPCHAT_ADS_CAMPAIGN_DEFS.length; i++) {
    const camp = SNAPCHAT_ADS_CAMPAIGN_DEFS[i]!;
    const cId = campaignUuid(camp.name);
    const campShare = normalizedShares[i]!;

    // Pick 2 of the 4 suffixes per campaign in a rotating pattern so all 4
    // bid strategies + a wide range of optimization goals appear across the
    // 12 rows.
    const adSquadTemplates = i % 2 === 0
      ? [adSquadSuffixes[0]!, adSquadSuffixes[1]!]
      : [adSquadSuffixes[2]!, adSquadSuffixes[3]!];

    for (let j = 0; j < adSquadTemplates.length; j++) {
      const tpl = adSquadTemplates[j]!;
      const asShare = j === 0 ? 0.60 : 0.40;
      const asName = `AS - ${tpl.suffix}`;
      const asId = adSquadUuid(camp.name, tpl.suffix);
      const factor = campShare * asShare;

      const asImpressions = Math.round(totalImpressionsAcc * factor);
      const asUniques = Math.round(totalUniquesAcc * factor);
      const asSwipes = Math.round(totalSwipesAcc * factor);
      const asSpend = roundCurrency(totalSpendAcc * factor);
      const asConversionPurchases = Math.round(
        totalConversionPurchasesAcc * factor
      );
      const asConversionPurchasesValue = roundCurrency(
        totalConversionPurchasesValueAcc * factor
      );
      const asTotalInstalls = Math.round(totalInstallsAcc * factor);

      records.push({
        _reportType: "adSquads",
        date: dayMultipliers[0]!.dateStr,
        _entityId: asId,
        _entityType: "AD_SQUAD",
        adsquadId: asId,
        adsquadName: asName,
        adsquadStatus: "ACTIVE",
        parentCampaignId: cId,
        bidStrategy: SNAPCHAT_ADS_BID_STRATEGIES[tpl.bidStrategyIndex]!,
        optimizationGoal:
          SNAPCHAT_ADS_OPTIMIZATION_GOALS[tpl.optimizationGoalIndex]!,

        impressions: asImpressions,
        uniques: asUniques,
        spend: asSpend,
        swipeUps: asSwipes,
        swipeUpRate: roundRate(safeDivide(asSwipes, asImpressions)),
        videoViews2s: Math.round(totalVideoViews2sAcc * factor),
        viewCompletion: Math.round(totalViewCompletionAcc * factor),
        totalInstalls: asTotalInstalls,
        conversionPurchases: asConversionPurchases,
        conversionPurchasesValue: asConversionPurchasesValue,
        conversionRate: roundRate(safeDivide(asConversionPurchases, asSwipes)),
      });
    }
  }

  // ============ 4. Ads (Promoted Snaps, 18 rows; ≥6 video, ≥2 AR_LENS) =====
  for (let i = 0; i < SNAPCHAT_ADS_CAMPAIGN_DEFS.length; i++) {
    const camp = SNAPCHAT_ADS_CAMPAIGN_DEFS[i]!;
    const cId = campaignUuid(camp.name);
    const campShare = normalizedShares[i]!;
    const creatives = SNAPCHAT_ADS_CREATIVE_NAMES_BY_CAMPAIGN[camp.name];
    if (!creatives) continue;

    for (let p = 0; p < creatives.length; p++) {
      const creative = creatives[p]!;
      const inCampShare =
        p === 0
          ? 0.50
          : p === 1
            ? 0.30
            : (1 - 0.80) / Math.max(1, creatives.length - 2);
      const factor = campShare * inCampShare;

      const adImpressions = Math.round(totalImpressionsAcc * factor);
      const adUniques = Math.round(totalUniquesAcc * factor);
      const adSwipes = Math.round(totalSwipesAcc * factor);
      const adSpend = roundCurrency(totalSpendAcc * factor);
      const adConversionPurchases = Math.round(
        totalConversionPurchasesAcc * factor
      );
      const adConversionPurchasesValue = roundCurrency(
        totalConversionPurchasesValueAcc * factor
      );

      const adIdValue = adUuid(creative.name);
      const cIdValue = creativeUuid(creative.name);
      const landingPageUrl = `${TRENDWEAR_DOMAIN}${creative.landingPath}`;

      // Video metrics only meaningful for video creative types.
      const isVideo =
        creative.creativeType === "STORY" ||
        creative.creativeType === "COMMERCIAL" ||
        creative.creativeType === "SNAP_AD";
      const adVideoViews2s = isVideo
        ? Math.round(totalVideoViews2sAcc * factor * 1.4)
        : 0;
      const adVideoQuartile1 = isVideo
        ? Math.round(adVideoViews2s * 0.42)
        : 0;
      const adVideoQuartile2 = isVideo
        ? Math.round(adVideoQuartile1 * 0.65)
        : 0;
      const adVideoQuartile3 = isVideo
        ? Math.round(adVideoQuartile2 * 0.65)
        : 0;
      const adViewCompletion = isVideo
        ? Math.round(adVideoQuartile3 * 0.55)
        : 0;

      records.push({
        _reportType: "ads",
        date: dayMultipliers[0]!.dateStr,
        _entityId: adIdValue,
        _entityType: "AD",
        adId: adIdValue,
        adName: `Ad - ${creative.name}`,
        adStatus: "ACTIVE",
        creativeId: cIdValue,
        creativeName: creative.name,
        creativeType: creative.creativeType,
        landingPageUrl,
        parentCampaignId: cId,

        impressions: adImpressions,
        uniques: adUniques,
        spend: adSpend,
        swipeUps: adSwipes,
        swipeUpRate: roundRate(safeDivide(adSwipes, adImpressions)),

        videoViews2s: adVideoViews2s,
        videoQuartile1: adVideoQuartile1,
        videoQuartile2: adVideoQuartile2,
        videoQuartile3: adVideoQuartile3,
        viewCompletion: adViewCompletion,

        conversionPurchases: adConversionPurchases,
        conversionPurchasesValue: adConversionPurchasesValue,
        conversionRate: roundRate(safeDivide(adConversionPurchases, adSwipes)),
      });
    }
  }

  // ============ 5. AgeGender (16 rows) =====================================
  // 15 standard rows (5 buckets × 3 genders) + 1 explicit `55+ × Female` row
  // exercising the Feb 2025 bucket migration code path.
  for (const seg of SNAPCHAT_ADS_AGE_GENDER_DISTRIBUTION) {
    const segImpressions = Math.max(
      SNAPCHAT_ADS_MIN_THRESHOLDS.impressions!,
      Math.round(totalImpressionsAcc * seg.share)
    );
    const segUniques = Math.max(
      SNAPCHAT_ADS_MIN_THRESHOLDS.uniques!,
      Math.round(totalUniquesAcc * seg.share)
    );
    const segSwipes = Math.max(
      SNAPCHAT_ADS_MIN_THRESHOLDS.swipes!,
      Math.round(totalSwipesAcc * seg.share)
    );
    const segSpend = roundCurrency(
      Math.max(SNAPCHAT_ADS_MIN_THRESHOLDS.spend!, totalSpendAcc * seg.share)
    );
    const segConversionPurchases = Math.round(
      totalConversionPurchasesAcc * seg.share
    );
    const segConversionPurchasesValue = roundCurrency(
      totalConversionPurchasesValueAcc * seg.share
    );

    records.push({
      _reportType: "ageGender",
      date: dayMultipliers[0]!.dateStr,
      ageBucket: seg.ageBucket,
      gender: seg.gender,

      impressions: segImpressions,
      uniques: segUniques,
      swipeUps: segSwipes,
      swipeUpRate: roundRate(safeDivide(segSwipes, segImpressions)),
      spend: segSpend,
      videoViews2s: Math.round(totalVideoViews2sAcc * seg.share),
      conversionPurchases: segConversionPurchases,
      conversionPurchasesValue: segConversionPurchasesValue,
    });
  }

  // ============ 6. CountryOs (10 rows) =====================================
  for (const cell of SNAPCHAT_ADS_COUNTRY_OS_DISTRIBUTION) {
    const cellImpressions = Math.max(
      SNAPCHAT_ADS_MIN_THRESHOLDS.impressions!,
      Math.round(totalImpressionsAcc * cell.share)
    );
    const cellUniques = Math.max(
      SNAPCHAT_ADS_MIN_THRESHOLDS.uniques!,
      Math.round(totalUniquesAcc * cell.share)
    );
    const cellSwipes = Math.max(
      SNAPCHAT_ADS_MIN_THRESHOLDS.swipes!,
      Math.round(totalSwipesAcc * cell.share)
    );
    const cellSpend = roundCurrency(
      Math.max(SNAPCHAT_ADS_MIN_THRESHOLDS.spend!, totalSpendAcc * cell.share)
    );
    const cellConversionPurchases = Math.round(
      totalConversionPurchasesAcc * cell.share
    );
    const cellConversionPurchasesValue = roundCurrency(
      totalConversionPurchasesValueAcc * cell.share
    );

    records.push({
      _reportType: "countryOs",
      date: dayMultipliers[0]!.dateStr,
      country: cell.country,
      os: cell.os,

      impressions: cellImpressions,
      uniques: cellUniques,
      swipeUps: cellSwipes,
      swipeUpRate: roundRate(safeDivide(cellSwipes, cellImpressions)),
      spend: cellSpend,
      videoViews2s: Math.round(totalVideoViews2sAcc * cell.share),
      totalInstalls: Math.round(totalInstallsAcc * cell.share),
      conversionPurchases: cellConversionPurchases,
      conversionPurchasesValue: cellConversionPurchasesValue,
    });
  }

  return {
    platform: "snapchat-ads",
    accountId: config.accountId,
    dateRange: { start: config.startDate, end: config.endDate },
    records,
    fetchedAt: config.endDate,
    recordCount: records.length,
    warnings,
    metadata: {
      adAccountId: accountId,
      currencyCode,
      timeZone,
      // Round 2 Pre-mortem E + Story 19.8 contract — orchestrator at
      // snapchat-ads.ts:1217 reads customEventLabels from ad-account
      // metadata in live mode; fixture mirrors the contract.
      customEventLabels: { ...SNAPCHAT_ADS_CUSTOM_EVENT_LABELS },
      // Stable across runs for determinism — uses the fixture window's
      // endDate, not Date.now().
      customEventLabelsCapturedAt: config.endDate.toISOString(),
      // Snap docs: "Metrics are finalized 48 hours after the end of the day
      // in the timezone set by the Ad Account."
      dataFinalizedThrough: new Date(
        config.endDate.getTime() - 48 * 60 * 60 * 1000
      ).toISOString(),
      fixtureGeneratedAt: config.endDate.toISOString(),
      // Round 5 Reflection 7: schema version pinned for stale-fixture
      // detection. Bump when the AdapterMetrics shape changes.
      fixtureSchemaVersion: SNAPCHAT_ADS_FIXTURE_SCHEMA_VERSION,
    },
  };
}
